import { useContext, useEffect } from 'react';
import { ChatContext } from '../context/ChatContext';
import CHAT_EVENTS from '../constants/ChatEvents';
import { useDispatch, useSelector } from 'react-redux';
import {
  markConversationAsRead,
  syncMessages,
} from 'src/redux/thunks/conversation';
import {
  addConversation,
  addMessage,
  finishConversation,
  joinGroupConversation,
  updateUserStatus,
} from 'src/redux/slices/chat';
import {
  discardSituationToProcess,
  removeCurrentSituation,
} from 'src/redux/slices/monitoring';

const useChat = (activeConversationId, userId) => {
  const { socket, situation } = useContext(ChatContext);
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!socket) return;

    const handleUserStatus = (userStatus) => {
      dispatch(updateUserStatus(userStatus));
    };

    const handleJoinChat = (newChat) => {
      dispatch(addConversation({ ...newChat, joined: true }));
    };

    const handleJoinGroup = (chat) => {
      dispatch(joinGroupConversation(chat));
    };

    const handleSendMessage = (message) => {
      dispatch(
        addMessage({
          ...message,
          currentUser: user.id,
        })
      );

      const msgAction = message?.message?.metaData?.action;
      if (msgAction === 'false_alarm_confirmed') {
        if (situation?.id) {
          dispatch(removeCurrentSituation(situation.id));
          dispatch(discardSituationToProcess());
        }
      }
    };

    const handleChatFinished = ({ conversationId }) => {
      dispatch(finishConversation({ conversationId }));
    };

    socket.on(CHAT_EVENTS.USER_STATUS, handleUserStatus);
    socket.on(CHAT_EVENTS.JOIN_CHAT, handleJoinChat);
    socket.on(CHAT_EVENTS.JOIN_GROUP, handleJoinGroup);
    socket.on(CHAT_EVENTS.SEND_MESSAGE, handleSendMessage);
    socket.on(CHAT_EVENTS.CHAT_FINISHED, handleChatFinished);

    return () => {
      socket.off(CHAT_EVENTS.USER_STATUS, handleUserStatus);
      socket.off(CHAT_EVENTS.JOIN_CHAT, handleJoinChat);
      socket.off(CHAT_EVENTS.JOIN_GROUP, handleJoinGroup);
      socket.off(CHAT_EVENTS.SEND_MESSAGE, handleSendMessage);
      socket.off(CHAT_EVENTS.CHAT_FINISHED, handleChatFinished);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const onReconnection = async () => {
      if (!activeConversationId || socket.sendBuffer.length > 0) return;

      try {
        await dispatch(
          syncMessages({
            conversationId: activeConversationId,
            userId,
          })
        ).unwrap();

        await dispatch(
          markConversationAsRead({
            conversationId: activeConversationId,
            userId,
          })
        ).unwrap();
      } catch (error) {
        console.log(error);
      }
    };

    socket.on('connect', onReconnection);

    return () => {
      socket.off('connect', onReconnection);
    };
  }, [socket, activeConversationId, dispatch, userId]);

  const sendMessage = async (message) => {
    const response = await socket
      .timeout(60 * 1000)
      .emitWithAck(CHAT_EVENTS.SEND_MESSAGE, message);

    if (!response.success) {
      throw new Error('Failed to send message');
    }

    return response;
  };

  const addUsersToGroup = async (users, conversationId) => {
    const response = await socket
      .timeout(60 * 1000)
      .emitWithAck(CHAT_EVENTS.JOIN_GROUP, {
        usersId: users,
        conversationId,
      });

    if (!response.success) {
      throw new Error('Failed to join group');
    }

    return response;
  };

  return {
    socket,
    sendMessage,
    addUsersToGroup,
    situation,
  };
};

export default useChat;
